"use client";

import { useEffect, useState } from "react";
import { X } from "lucide-react";

import { ReloadAppButton } from "@/components/system/ReloadAppButton";
import { APP_UPDATED_TITLE } from "@/lib/stale-deploy";

// Gentle "update available" banner, mounted in every app via
// StaleAppBannerMount. Skew Protection keeps an old page talking to its own
// deployment for 7 days, then its Server Actions 404 (lib/stale-deploy.ts).
// Outlet tablets keep the PWA open for days, so we ask staff to reload BEFORE
// anything fails: compare this page's deployment id (Next's boot-time id, else the server render)
// with the one /api/version reports. Checks on mount, when the tab comes back
// into view, and when the network returns — never on a timer.
//
// Fail-quiet by design: this is advice, not data. No id, a failed fetch, a
// non-OK answer or bad JSON all mean "say nothing" — never a wrong warning.

export const STALE_BANNER_DISMISS_KEY = "fb:stale-app-banner-dismissed";
export const STALE_CHECK_MIN_GAP_MS = 60_000;

// Dismissal is remembered PER live deployment: hiding "update X" must not
// silence update Y on a tablet that keeps the tab open for days.
function readDismissedId(): string | null {
  try {
    return window.sessionStorage.getItem(STALE_BANNER_DISMISS_KEY);
  } catch {
    return null;
  }
}

function writeDismissedId(liveId: string): void {
  try {
    window.sessionStorage.setItem(STALE_BANNER_DISMISS_KEY, liveId);
  } catch {
    // Storage blocked (private mode etc.) — the banner still hides for this page.
  }
}

async function fetchLiveDeploymentId(): Promise<string | null> {
  try {
    const res = await fetch("/api/version", { cache: "no-store" });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    if (!body || typeof body !== "object") return null;
    const id = (body as { deploymentId?: unknown }).deploymentId;
    return typeof id === "string" && id ? id : null;
  } catch {
    return null;
  }
}

/** This page's deployment. Next reads <html data-dpl-id> at boot, deletes the
 *  attribute and keeps the value on globalThis.NEXT_DEPLOYMENT_ID
 *  (next/dist/client/register-deployment-id-global.js) — the exact id its own
 *  requests are pinned to. The server-rendered id is only a fallback. */
function pageDeploymentId(serverId: string | null): string | null {
  const booted = (globalThis as { NEXT_DEPLOYMENT_ID?: unknown }).NEXT_DEPLOYMENT_ID;
  return typeof booted === "string" && booted ? booted : serverId;
}

export function StaleAppBanner({ currentId: serverId }: { currentId: string | null }) {
  const [liveId, setLiveId] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const currentId = pageDeploymentId(serverId);
    if (!currentId) return;
    let alive = true;
    let found = false;
    let lastCheckAt: number | null = null;

    const check = () => {
      if (found) return;
      const now = Date.now();
      if (lastCheckAt !== null && now - lastCheckAt < STALE_CHECK_MIN_GAP_MS) return;
      lastCheckAt = now;
      void fetchLiveDeploymentId().then((live) => {
        if (!alive) return;
        // A failed check must not eat the next minute's retry (e.g. `online`).
        if (!live) {
          lastCheckAt = null;
          return;
        }
        if (live === currentId || live === readDismissedId()) return;
        found = true;
        setLiveId(live);
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };

    check();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", check);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", check);
    };
  }, [serverId]);

  if (!liveId || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-stale-app-banner
      // Supply's theme defines --accent as HEX, so hsl(var(--accent)) would be
      // invalid there (and ReloadAppButton transparent). Re-state Supply's
      // orange (#e85d00) as an HSL triplet for this subtree only.
      className="print:hidden fixed inset-x-0 z-[60] mx-auto flex w-[calc(100%-24px)] max-w-[420px] items-center gap-2.5 rounded-[var(--radius-sm)] border-l-4 py-2 pl-3 pr-1.5 shadow-lg [[data-supply]_&]:[--accent:24_100%_45%]"
      style={{
        top: "calc(env(safe-area-inset-top, 0px) + 8px)",
        backgroundColor: "hsl(var(--bg))",
        color: "hsl(var(--fg))",
        borderLeftColor: "hsl(var(--accent))",
        outline: "1px solid hsl(var(--fg) / 0.12)",
      }}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-extrabold leading-tight">{APP_UPDATED_TITLE}</p>
        <p className="mt-0.5 text-[12px] leading-snug" style={{ color: "hsl(var(--fg-muted))" }}>
          Tap Reload to get the newest version.
        </p>
      </div>
      <ReloadAppButton className="shrink-0" />
      <button
        type="button"
        aria-label="Hide this message"
        data-bc="Hide app update banner"
        onClick={() => {
          writeDismissedId(liveId);
          setDismissed(true);
        }}
        className="grid size-8 shrink-0 place-items-center rounded-full transition active:scale-[0.95]"
        style={{ color: "hsl(var(--fg-muted))" }}
      >
        <X className="size-4" strokeWidth={2.4} aria-hidden />
      </button>
    </div>
  );
}
